// Organisation defaults and the operator's notification preferences.
// Route: /settings

import { useEffect, useState } from "react";

import { http } from "../api";
import { useSession } from "../App";
import {
  Field,
  SkeletonRows,
  Spinner,
  Tabs,
  Toggle,
  useAsync,
  useToast,
} from "../ui";
import { errMessage, ErrorBanner } from "./shared";

// ============================================================ settings

/** The admin screens. They have routes but no place in the sidebar, so without these
 * tiles the only way to "add a user" was to know the URL. Each shows only if the
 * operator's role can open it - the route guards in App.tsx are unchanged. */
const ADMIN_LINKS: { label: string; to: string; icon: string; permission: string }[] = [
  { label: "Users", to: "/settings/users", icon: "◍", permission: "users" },
  { label: "Roles", to: "/settings/roles", icon: "◇", permission: "roles" },
  { label: "Teams", to: "/settings/teams", icon: "◎", permission: "teams" },
  { label: "Contacts", to: "/settings/contacts", icon: "☏", permission: "contacts" },
  { label: "Tags", to: "/settings/tags", icon: "◈", permission: "tags" },
  { label: "WhatsApp accounts", to: "/settings/accounts", icon: "▤", permission: "accounts" },
  { label: "WhatsApp flows", to: "/settings/flows", icon: "⇄", permission: "settings.general" },
  { label: "Custom actions", to: "/settings/custom-actions", icon: "✧", permission: "settings.general" },
  { label: "Webhooks", to: "/settings/webhooks", icon: "⚡", permission: "settings.general" },
  { label: "API keys", to: "/settings/api-keys", icon: "⚿", permission: "settings.general" },
  { label: "Single sign-on", to: "/settings/sso", icon: "⛨", permission: "settings.general" },
  { label: "Audit logs", to: "/settings/audit-logs", icon: "☰", permission: "settings.general" },
];

interface OrgSettingsBlob {
  timezone?: string;
  date_format?: string;
  mask_phone_numbers?: boolean;
  chat_retention_days?: number;
  graph_retention_days?: number;
  telemetry_retention_days?: number;
  audit_retention_days?: number;
  feed_file_retention_days?: number;
}

interface OrgSettingsPayload {
  name?: string;
  settings?: OrgSettingsBlob;
}

interface NotificationSettings {
  email_notifications: boolean;
  new_message_alerts: boolean;
}

interface MePayload {
  settings?: Partial<NotificationSettings>;
}

interface GeneralForm {
  organization_name: string;
  default_timezone: string;
  date_format: string;
  mask_phone_numbers: boolean;
  chat_retention_days: number;
  graph_retention_days: number;
  telemetry_retention_days: number;
  audit_retention_days: number;
  feed_file_retention_days: number;
}

const EMPTY_GENERAL: GeneralForm = {
  organization_name: "My Organization",
  default_timezone: "UTC",
  date_format: "YYYY-MM-DD",
  mask_phone_numbers: false,
  chat_retention_days: 0,
  graph_retention_days: 365,
  telemetry_retention_days: 90,
  audit_retention_days: 365,
  feed_file_retention_days: 30,
};

const TIMEZONES: { value: string; label: string }[] = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "Europe/London", label: "London" },
  { value: "Asia/Tokyo", label: "Tokyo" },
];

const DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"];

export function SettingsView() {
  const { can, refresh } = useSession();
  const toast = useToast();
  const canWrite = can("settings.general", "write");

  const [tab, setTab] = useState("general");
  const [general, setGeneral] = useState<GeneralForm>(EMPTY_GENERAL);
  const [notify, setNotify] = useState<NotificationSettings>({
    email_notifications: true,
    new_message_alerts: true,
  });
  const [saving, setSaving] = useState(false);

  const { data, error, loading, reload } = useAsync(
    async () => {
      const [org, me] = await Promise.all([
        http.get<OrgSettingsPayload>("/org/settings"),
        http.get<MePayload>("/me"),
      ]);
      return { org, me };
    },
    []
  );

  useEffect(() => {
    if (!data) return;
    const blob = data.org.settings ?? {};
    setGeneral({
      organization_name: data.org.name || "My Organization",
      default_timezone: blob.timezone || "UTC",
      date_format: blob.date_format || "YYYY-MM-DD",
      mask_phone_numbers: blob.mask_phone_numbers === true,
      // The server sends 0 for "not configured", so 0 falls back to the product
      // default on every retention field except chat, where 0 means keep forever.
      chat_retention_days: blob.chat_retention_days || 0,
      graph_retention_days: blob.graph_retention_days || 365,
      telemetry_retention_days: blob.telemetry_retention_days || 90,
      audit_retention_days: blob.audit_retention_days || 365,
      feed_file_retention_days: blob.feed_file_retention_days || 30,
    });
    const s = data.me.settings ?? {};
    setNotify({
      email_notifications: s.email_notifications ?? true,
      new_message_alerts: s.new_message_alerts ?? true,
    });
  }, [data]);

  const saveGeneral = async () => {
    setSaving(true);
    try {
      await http.put("/org/settings", {
        name: general.organization_name,
        timezone: general.default_timezone,
        date_format: general.date_format,
        mask_phone_numbers: general.mask_phone_numbers,
        chat_retention_days: general.chat_retention_days,
        graph_retention_days: general.graph_retention_days,
        telemetry_retention_days: general.telemetry_retention_days,
        audit_retention_days: general.audit_retention_days,
        feed_file_retention_days: general.feed_file_retention_days,
      });
      toast.push("General settings saved", "good");
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not save the settings"), "critical");
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await http.put("/me/settings", notify);
      toast.push("Notification settings saved", "good");
      await refresh();
    } catch (err) {
      toast.push(errMessage(err, "Could not save the notification settings"), "critical");
    } finally {
      setSaving(false);
    }
  };

  const num = (value: number, set: (n: number) => void) => (
    <input
      type="number"
      min={0}
      value={value}
      onChange={(e) => set(Math.max(0, Number(e.target.value) || 0))}
    />
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">Organisation defaults, retention windows and your notifications.</div>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the settings" />

      {ADMIN_LINKS.some((l) => can(l.permission)) && (
        <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
          <div className="card-head">
            <h3>Administration</h3>
          </div>
          <div className="card-body">
            <div className="shortcuts">
              {ADMIN_LINKS.filter((l) => can(l.permission)).map((l) => (
                <a className="shortcut" key={l.to} href={l.to}>
                  <span className="glyph" aria-hidden>
                    {l.icon}
                  </span>
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      <Tabs
        tabs={[
          { id: "general", label: "General" },
          { id: "notifications", label: "Notifications" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {loading ? (
        <div className="card">
          <div className="card-body">
            <SkeletonRows n={6} cols={2} />
          </div>
        </div>
      ) : tab === "general" ? (
        <div className="card" id="panel-general" role="tabpanel" aria-labelledby="tab-general">
          <div className="card-head">
            <h3>General settings</h3>
          </div>
          <div className="card-body">
            <Field label="Organisation name">
              <input
                value={general.organization_name}
                onChange={(e) => setGeneral({ ...general, organization_name: e.target.value })}
              />
            </Field>

            <div className="grid c2">
              <Field label="Default timezone">
                <select
                  value={general.default_timezone}
                  onChange={(e) => setGeneral({ ...general, default_timezone: e.target.value })}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date format">
                <select
                  value={general.date_format}
                  onChange={(e) => setGeneral({ ...general, date_format: e.target.value })}
                >
                  {DATE_FORMATS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <hr className="sep" />

            <div className="setting">
              <div className="text">
                <span className="strong">Mask phone numbers</span>
                <p>Hide the middle digits of every contact number across the console.</p>
              </div>
              <Toggle
                checked={general.mask_phone_numbers}
                onChange={(v) => setGeneral({ ...general, mask_phone_numbers: v })}
                ariaLabel="Mask phone numbers"
              />
            </div>

            <hr className="sep" />

            <Field label="Chat retention" hint="0 keeps conversations forever.">
              <div className="row">
                <div style={{ width: 128 }}>
                  {num(general.chat_retention_days, (n) =>
                    setGeneral({ ...general, chat_retention_days: n })
                  )}
                </div>
                <span className="muted">days</span>
              </div>
            </Field>

            <hr className="sep" />

            <div className="section">
              <h4>Intelligence retention</h4>
              <p className="muted" style={{ fontSize: 12 }}>
                How long the graph, telemetry, audit trail and downloaded feed files are kept.
              </p>
              <div className="grid c2">
                <Field label="Graph retention (days)">
                  {num(general.graph_retention_days, (n) =>
                    setGeneral({ ...general, graph_retention_days: n })
                  )}
                </Field>
                <Field label="Telemetry retention (days)">
                  {num(general.telemetry_retention_days, (n) =>
                    setGeneral({ ...general, telemetry_retention_days: n })
                  )}
                </Field>
                <Field label="Audit retention (days)">
                  {num(general.audit_retention_days, (n) =>
                    setGeneral({ ...general, audit_retention_days: n })
                  )}
                </Field>
                <Field label="Feed file retention (days)">
                  {num(general.feed_file_retention_days, (n) =>
                    setGeneral({ ...general, feed_file_retention_days: n })
                  )}
                </Field>
              </div>
            </div>

            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn primary" onClick={() => void saveGeneral()} disabled={saving || !canWrite}>
                {saving && <Spinner label="Saving" />}
                Save
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card" id="panel-notifications" role="tabpanel" aria-labelledby="tab-notifications">
          <div className="card-head">
            <h3>Notifications</h3>
          </div>
          <div className="card-body">
            <div className="setting">
              <div className="text">
                <span className="strong">Email notifications</span>
                <p>Summaries and alerts sent to {"your inbox"}.</p>
              </div>
              <Toggle
                checked={notify.email_notifications}
                onChange={(v) => setNotify({ ...notify, email_notifications: v })}
                ariaLabel="Email notifications"
              />
            </div>
            <div className="setting">
              <div className="text">
                <span className="strong">New message alerts</span>
                <p>Notify me when a conversation I own receives a message.</p>
              </div>
              <Toggle
                checked={notify.new_message_alerts}
                onChange={(v) => setNotify({ ...notify, new_message_alerts: v })}
                ariaLabel="New message alerts"
              />
            </div>

            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn primary" onClick={() => void saveNotifications()} disabled={saving}>
                {saving && <Spinner label="Saving" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
